import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    InjectionToken,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Event as NgRouterEvent, NavigationEnd, Router } from '@angular/router';
import { DarkEntryFieldComponent } from '@app/shared/common/dark-entry/dark-entry-field.component';
import { PrefersReducedMotionService } from '@app/shared/common/motion/prefers-reduced-motion.service';
import {
    asyncScheduler,
    BehaviorSubject,
    combineLatest,
    defer,
    EMPTY,
    merge,
    Observable,
    of,
    SchedulerLike,
    Subject,
} from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import {
    buildHandoffOverlayVm,
    handoffTimeline,
    HandoffOverlayVm,
    HandoffPhase,
    hasLeftOnboarding,
    runHandoff,
} from './handoff.logic';
import { OnboardingHandoffService } from './onboarding-handoff.service';

/** The scheduler of the handoff's timers: RxJS's `asyncScheduler` (real time), unless a spec provides a virtual one. */
export const ONBOARDING_HANDOFF_SCHEDULER = new InjectionToken<SchedulerLike>('ONBOARDING_HANDOFF_SCHEDULER', {
    providedIn: 'root',
    factory: () => asyncScheduler,
});

/**
 * `vf-onboarding-handoff-overlay` — the onboarding handoff into My Day (B-11; plan §3.2 "Handoff", D17; cases OB-H-01,
 * OB-H-02, OB-H-04, OB-O-02, OB-G-12). P0-13-b placed it once in app.component.html, outside the routed area, and
 * imported it in app.module.ts, so it stays mounted while the route changes from `/app/onboarding` to My Day.
 *
 * While `OnboardingHandoffService.active$` is true it runs one handoff (handoff.logic.ts `runHandoff`): it covers the
 * screen with the dark stage as Organize left it (the background field and the rail), the field's cards line up into
 * rows, the rail fades, and once the router has arrived outside onboarding the dark stage crossfades into the real,
 * light My Day and its sidebar. The created tasks then stay highlighted for HANDOFF_HIGHLIGHT_MS, counted from when My
 * Day has drawn one of their rows (FX-7, `highlightShown$`; at most HANDOFF_HIGHLIGHT_SHOWN_MAX_WAIT_MS later), and the
 * overlay calls `end()`. Under reduced motion nothing is drawn and nothing moves: an instant switch (OB-H-04), with the still
 * highlight. A handoff that ends early (the Organize screen's failed exit calls `end()`) stops at once.
 *
 * The overlay never navigates, never marks completion and never touches the route guards: the exit that finished
 * onboarding does that, in D-08's completion contract order (Complete answered, `begin`, the in-memory mark, then
 * `navigateByUrl('/app/main/my-day', { replaceUrl: true })`), so Back never returns to onboarding (OB-H-05).
 *
 * Styles: ViewEncapsulation.None, every selector `vf-` prefixed. The stylesheet also holds My Day's
 * `vf-onboarding-new-row` highlight (ag-Grid rows sit outside every component's encapsulation), so nothing is added to
 * the global style.css. The layer is decorative (`aria-hidden`); it takes clicks only while the screen under it is
 * dark and changing, and never longer than HANDOFF_ARRIVAL_MAX_WAIT_MS.
 *
 * Standalone and OnPush; the template reads `view$` through the async pipe; the one subscription ends with the
 * component (`takeUntil(destroy$)`). No DataService channel, no DOM query.
 */
@Component({
    selector: 'vf-onboarding-handoff-overlay',
    standalone: true,
    imports: [CommonModule, DarkEntryFieldComponent],
    templateUrl: './handoff-overlay.component.html',
    styleUrls: ['./handoff-overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class OnboardingHandoffOverlayComponent implements OnInit, OnDestroy {
    /** What the overlay draws. */
    readonly view$: Observable<HandoffOverlayVm>;

    private readonly destroy$ = new Subject<void>();
    private readonly phase$ = new BehaviorSubject<HandoffPhase>('idle');

    constructor(
        private readonly handoff: OnboardingHandoffService,
        private readonly router: Router,
        private readonly motion: PrefersReducedMotionService,
        @Inject(ONBOARDING_HANDOFF_SCHEDULER) private readonly scheduler: SchedulerLike
    ) {
        this.view$ = combineLatest([this.phase$, motion.reduced$]).pipe(
            map(([phase, still]: [HandoffPhase, boolean]) => buildHandoffOverlayVm(phase, still))
        );
    }

    ngOnInit(): void {
        this.handoff.active$
            .pipe(
                switchMap((active: boolean) => (active ? this.runOne() : of<HandoffPhase>('idle'))),
                takeUntil(this.destroy$)
            )
            .subscribe((phase: HandoffPhase) => this.onPhase(phase));
    }

    trackRailItem(_index: number, item: string): string {
        return item;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.phase$.complete();
        // The host went away mid-handoff (the app shell was left): end it, so a later shell never replays it.
        if (this.handoff.isActive) {
            this.handoff.end();
        }
    }

    // ---- internals --------------------------------------------------------------------------------------------------

    /**
     * One handoff, timed for the current motion setting. FX-7: the highlight's time starts once My Day has drawn a
     * highlighted task's row (the service's replaying flag), within the timeline's cap.
     */
    private runOne(): Observable<HandoffPhase> {
        return runHandoff(
            handoffTimeline(this.motion.reduced),
            this.arrival(),
            this.handoff.highlightShown$.pipe(filter((shown: boolean) => shown)),
            this.scheduler
        );
    }

    /**
     * Emits when the router is outside onboarding: at once when it already is (a caller that navigated first), else at
     * the first NavigationEnd that lands outside it (My Day, after the exit's replaceUrl navigation).
     */
    private arrival(): Observable<boolean> {
        return merge(
            defer(() => (hasLeftOnboarding(this.router.url) ? of(true) : EMPTY)),
            this.router.events.pipe(
                filter((event: NgRouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
                filter((event: NavigationEnd) => hasLeftOnboarding(event.urlAfterRedirects || event.url)),
                map((): boolean => true)
            )
        );
    }

    /** Shows a phase; when the highlight is over the handoff ends (the hold stays for this sign-in). */
    private onPhase(phase: HandoffPhase): void {
        this.phase$.next(phase);
        if (phase === 'done') {
            this.handoff.end();
        }
    }
}
